import { Link, useNavigate } from '@tanstack/react-router'
import { useState, type ReactNode } from 'react'

import { useAuth } from '~/auth/auth-context'
import { Spinner } from '~/components/working'
import { reached, useBeat } from '~/components/use-work'
import { ConfirmDialog } from '~/components/confirm-dialog'
import { PanelHeader } from '~/components/panel-header'
import { DataSection, ExportPanel, ImportPanel } from '~/components/data-panels'
import { DialogLink } from '~/components/dialog-link'
import {
  BodyweightIcon,
  ChevronRightIcon,
  DeleteIcon,
  DiaryIcon,
  DownloadIcon,
  FeedbackIcon,
  ScaleIcon,
  SignOutIcon,
  StorageIcon,
  VideoIcon,
} from '~/components/icons'
import { Skeleton, SkeletonBlock } from '~/components/skeleton'
import { useApi } from '~/data/data-context'
import {
  MEASUREMENT_LABELS,
  type Measurement,
  ROUTINE_CHOICES,
  SLEEP_CHOICE,
  SLEEP_MEASUREMENTS,
} from '~/data/weighins'
import { HeightInput } from '~/components/height-input'
import { ACTIVITY_LABELS, ACTIVITY_LEVELS, currentGoal, type ActivityLevel } from '~/data/nutrition'
import { keepGoal, reloadNutrition, useNutrition } from '~/data/nutrition-store'
import { useToast } from '~/components/toast-context'
import { saveWeighInReminder, useWeighInReminder } from '~/lib/weighin-settings'
import { formatBytes } from '~/lib/dates'
import { DIALOG_PATHS, withoutDialog, type SettingsSection } from '~/lib/dialogs'
import { weekOrder } from '~/lib/weekdays'
import { FACES_DEFAULTS } from '@gymcloud/faces'
import { START_LABELS } from '~/lib/faces-plan'

/** What each section is called and what it holds, in the order the list offers them. */
const SECTIONS: {
  section: SettingsSection
  title: string
  description: string
  icon: ReactNode
  danger?: boolean
}[] = [
  {
    section: 'units',
    title: 'Units',
    description: 'Kilograms or pounds, wherever weight is shown or entered.',
    icon: <ScaleIcon aria-hidden className="size-7 text-ink-muted" />,
  },
  {
    section: 'videos',
    title: 'Videos',
    description: 'Whose faces a video you download blurs, and whether it asks.',
    icon: <VideoIcon aria-hidden className="size-7 text-ink-muted" />,
  },
  {
    section: 'weighIn',
    title: 'Weigh-in',
    description: 'What a day logs, and the reminders that ask for it.',
    icon: <BodyweightIcon aria-hidden className="size-7 text-ink-muted" />,
  },
  {
    section: 'nutrition',
    title: 'Nutrition',
    description: 'Your body stats, your own foods and recipes, and the goal.',
    icon: <DiaryIcon aria-hidden className="size-7 text-ink-muted" />,
  },
  {
    section: 'storage',
    title: 'Storage',
    description: 'How much room your videos take up.',
    icon: <StorageIcon aria-hidden className="size-7 text-ink-muted" />,
  },
  {
    section: 'data',
    title: 'Data',
    description: 'Export everything you have logged, or bring in another app’s.',
    icon: <DownloadIcon aria-hidden className="size-7 text-ink-muted" />,
  },
  {
    section: 'delete-account',
    title: 'Delete account',
    description: 'Remove your account and everything in it.',
    icon: <DeleteIcon aria-hidden className="size-7 text-danger" />,
    danger: true,
  },
]

/**
 * Settings, in the corner card, and whichever of its sections is open. A section is a page
 * inside it rather than a panel of its own — the card just morphs between the two, and the
 * heading's back walks it out the way it came.
 */
export function SettingsPanel({
  section,
  onBack,
}: {
  /** Null while the list of sections is what is open. */
  section: SettingsSection | null
  /** Out of the whole of settings, which is out of the dialog it stands in. */
  onBack: () => void
}) {
  const navigate = useNavigate()
  const { signOut } = useAuth()
  const [signingOut, setSigningOut] = useState(false)
  const open = SECTIONS.find((entry) => entry.section === section) ?? null

  function toSection(which: 'settings' | 'data'): void {
    void navigate({
      to: '.',
      search: (prev) => ({ ...prev, dialog: which }),
      mask: { to: DIALOG_PATHS[which] },
      // The page beneath never moves for a step inside the card, per the design spec.
      resetScroll: false,
      viewTransition: false,
    })
  }

  function toList(): void {
    toSection('settings')
  }

  // The two processes the Data section leads to are pages of their own inside the card, each
  // wearing its own heading and stepping back to the section rather than to the list.
  if (section === 'export') {
    return (
      <ExportPanel
        onBack={() => {
          toSection('data')
        }}
      />
    )
  }
  if (section === 'import') {
    return (
      <ImportPanel
        onBack={() => {
          toSection('data')
        }}
        onDone={() => {
          toSection('data')
        }}
      />
    )
  }

  if (open === null) {
    return (
      <>
        <PanelHeader title="Settings" close onBack={onBack} />
        <nav className="w-88 p-2 pt-1">
          {SECTIONS.map((entry) => (
            <DialogLink key={entry.section} dialog={entry.section} className="menu-row">
              {entry.icon}
              <span className="min-w-0 flex-1">
                <span className="block font-medium">{entry.title}</span>
                <span className="block text-sm text-ink-muted">{entry.description}</span>
              </span>
              <ChevronRightIcon aria-hidden className="size-6 shrink-0 text-ink-muted" />
            </DialogLink>
          ))}

          {/*
            Under the sections and apart from them: neither is a section of settings — one is a
            page about the app rather than a setting of it, and the other is a way out of the
            account altogether. They stand here because settings is what every page reaches
            through the account, and they were reached through the account before.
          */}
          <div className="my-2 border-t border-white/10" />
          <DialogLink dialog="feedback" className="menu-row">
            <FeedbackIcon aria-hidden className="size-7 shrink-0 text-ink-muted" />
            <span className="min-w-0 flex-1 font-medium">Feedback</span>
          </DialogLink>
          <button
            type="button"
            className="menu-row"
            onClick={() => {
              setSigningOut(true)
            }}
          >
            <SignOutIcon aria-hidden className="size-7 shrink-0 text-ink-muted" />
            <span className="min-w-0 flex-1 text-left font-medium">Sign out</span>
          </button>
        </nav>

        {/* Signing out asks first — the app spec, You — and answering no leaves it signed in. */}
        {signingOut && (
          <ConfirmDialog
            title="Sign out?"
            body="This browser stops holding your account until you sign in again."
            confirm="Sign out"
            onConfirm={signOut}
            onDismiss={() => {
              setSigningOut(false)
            }}
          />
        )}
      </>
    )
  }

  return (
    <>
      <PanelHeader title={open.title} danger={open.danger ?? false} onBack={toList} />
      <div className="w-88 px-2 pb-2">
        {open.section === 'units' ? <UnitsSection /> : null}
        {open.section === 'videos' ? <VideosSection /> : null}
        {open.section === 'weighIn' ? <WeighInSection /> : null}
        {open.section === 'nutrition' ? <NutritionSection /> : null}
        {open.section === 'storage' ? <StorageSection /> : null}
        {open.section === 'data' ? <DataSection /> : null}
        {open.section === 'delete-account' ? <DeleteAccountSection /> : null}
      </div>
    </>
  )
}

/**
 * The daily weigh-in, tuned: which girths sit in front of the measurements fold, whether the
 * browser asks for the day's weigh-in and when, and whether the page carries the nudge while
 * today is unlogged. What a day logs and the nudge are the account's and follow it anywhere;
 * the reminder is this browser's own, because it is this browser that would have to post it.
 */
function WeighInSection() {
  const { settings, setSettings } = useAuth()
  const reminder = useWeighInReminder()

  const enableReminder = (enabled: boolean) => {
    saveWeighInReminder({ ...reminder, enabled })
    if (enabled && typeof Notification !== 'undefined' && Notification.permission === 'default') {
      void Notification.requestPermission()
    }
  }

  const blocked =
    reminder.enabled && typeof Notification !== 'undefined' && Notification.permission === 'denied'

  return (
    <>
      <fieldset>
        <legend className="px-3 pt-1 pb-1 text-xs text-ink-muted">What you log</legend>
        <label className="flex items-center justify-between rounded-xl px-3 py-2.5">
          <span className="flex items-baseline gap-2">
            Body weight
            <span className="text-xs text-ink-muted">always</span>
          </span>
          <input
            type="checkbox"
            aria-label="Body weight"
            checked
            disabled
            className="h-5 w-5 accent-accent"
          />
        </label>
        {/*
          Sleep stands as one choice with two halves behind it. It is kept as two readings —
          when you went to bed and when you got up — but nobody logs one of those: a bedtime
          with no wake time is half a night, so the two are chosen together.
        */}
        {ROUTINE_CHOICES.map((name) => {
          const said = name === SLEEP_CHOICE ? 'Sleep' : MEASUREMENT_LABELS[name]
          const moving: readonly Measurement[] =
            name === SLEEP_CHOICE ? [...SLEEP_MEASUREMENTS] : [name]
          return (
            <label
              key={name}
              className="flex cursor-pointer items-center justify-between rounded-xl px-3 py-2.5 transition hover:bg-hover"
            >
              {said}
              <input
                type="checkbox"
                aria-label={said}
                checked={settings.weighInRoutine.includes(name)}
                onChange={(event) => {
                  void setSettings({
                    weighInRoutine: event.target.checked
                      ? [...settings.weighInRoutine, ...moving]
                      : settings.weighInRoutine.filter(
                          (held) => !(moving as readonly string[]).includes(held),
                        ),
                  })
                }}
                className="h-5 w-5 accent-accent"
              />
            </label>
          )
        })}
      </fieldset>
      <p className="px-3 pt-2 pb-3 text-xs text-ink-muted">
        What is checked sits in front on the weigh-in; the rest waits behind More measurements.
      </p>

      <fieldset>
        <legend className="px-3 pt-1 pb-1 text-xs text-ink-muted">Reminder</legend>
        <label className="flex cursor-pointer items-center justify-between rounded-xl px-3 py-2.5 transition hover:bg-hover">
          Daily reminder
          <input
            type="checkbox"
            aria-label="Daily reminder"
            checked={reminder.enabled}
            onChange={(event) => {
              enableReminder(event.target.checked)
            }}
            className="h-5 w-5 accent-accent"
          />
        </label>
        {reminder.enabled && (
          <label className="flex items-center justify-between rounded-xl px-3 py-2.5">
            Time
            <input
              type="time"
              aria-label="Reminder time"
              value={reminder.time}
              onChange={(event) => {
                saveWeighInReminder({ ...reminder, time: event.target.value })
              }}
              className="rounded-lg bg-surface px-2 py-1.5 text-sm outline-none ring-1 ring-white/10 focus:ring-2 focus:ring-accent"
            />
          </label>
        )}
      </fieldset>
      <p className="px-3 pt-2 pb-3 text-xs text-ink-muted">
        {blocked
          ? 'Notifications are blocked in this browser, so the reminder has no way to reach you.'
          : 'A browser notification at the chosen time, asking for the day that has none yet.'}
      </p>

      <fieldset>
        <legend className="px-3 pt-1 pb-1 text-xs text-ink-muted">Nudge</legend>
        <label className="flex cursor-pointer items-center justify-between rounded-xl px-3 py-2.5 transition hover:bg-hover">
          Weigh-in nudge
          <input
            type="checkbox"
            aria-label="Weigh-in nudge"
            checked={settings.weighInNudge}
            onChange={(event) => {
              void setSettings({ weighInNudge: event.target.checked })
            }}
            className="h-5 w-5 accent-accent"
          />
        </label>
        {settings.weighInNudge && (
          <label className="flex items-center justify-between rounded-xl px-3 py-2.5">
            From
            <input
              type="time"
              aria-label="Nudge from"
              value={clockOf(settings.weighInNudgeFrom)}
              onChange={(event) => {
                const minutes = minutesOf(event.target.value)
                if (minutes !== null) {
                  void setSettings({ weighInNudgeFrom: minutes })
                }
              }}
              className="rounded-lg bg-surface px-2 py-1.5 text-sm outline-none ring-1 ring-white/10 focus:ring-2 focus:ring-accent"
            />
          </label>
        )}
      </fieldset>
      <p className="px-3 pt-2 pb-2 text-xs text-ink-muted">
        A note under the account from this hour on, while today's weigh-in is unlogged.
      </p>
    </>
  )
}

/**
 * A minute of the day as a clock and back — what a time field reads and writes, against the
 * minutes past midnight the setting is kept as. A time is kept as a number because a clock is
 * a way of writing one, and half the world writes it differently.
 */
function clockOf(minutes: number): string {
  const pad = (value: number) => String(value).padStart(2, '0')
  return `${pad(Math.floor(minutes / 60))}:${pad(minutes % 60)}`
}

function minutesOf(clock: string): number | null {
  const [hours, minutes] = clock.split(':').map(Number)
  if (hours === undefined || minutes === undefined || !Number.isFinite(hours + minutes)) {
    return null
  }
  return hours * 60 + minutes
}

/**
 * The nutrition section, per the spec: the body stats the expenditure is estimated from —
 * date of birth, sex, height, each of which can be left empty — the activity level, the way
 * to the account's own foods and to the goal, and the weekday of the weekly check-in where there
 * is a goal. Everything here belongs to the account.
 */
function NutritionSection() {
  const { settings, setSettings, distanceUnits } = useAuth()
  const api = useApi()
  const toast = useToast()
  const data = useNutrition()
  const goal = data === null ? null : currentGoal(data.goals)
  const [savingWeekday, setSavingWeekday] = useState(false)
  const weekdayShown = useBeat(savingWeekday)

  const changeWeekday = async (weeklyCheckinWeekday: number) => {
    if (goal === null) {
      return
    }
    setSavingWeekday(true)
    try {
      // Changed on the goal as it stands: the goal stays the one it is, with its history.
      const saved = await api.changeCheckinWeekday(weeklyCheckinWeekday)
      keepGoal(saved)
      reloadNutrition()
    } catch {
      toast('That did not reach us. Try again in a moment.')
    } finally {
      setSavingWeekday(false)
    }
  }

  const field =
    'rounded-lg bg-surface px-2 py-1.5 text-sm text-ink outline-none ring-1 ring-white/10 focus:ring-2 focus:ring-accent'

  return (
    <>
      <fieldset>
        <legend className="px-3 pt-1 pb-1 text-xs text-ink-muted">Body stats</legend>
        <label className="flex items-center justify-between rounded-xl px-3 py-2.5">
          Date of birth
          <input
            type="date"
            aria-label="Date of birth"
            value={settings.birthDate ?? ''}
            onChange={(event) => {
              const next = event.target.value
              void setSettings({ birthDate: /^\d{4}-\d{2}-\d{2}$/.test(next) ? next : null })
            }}
            className={field}
          />
        </label>
        <label className="flex items-center justify-between rounded-xl px-3 py-2.5">
          Sex
          <select
            aria-label="Sex"
            value={settings.sex ?? ''}
            onChange={(event) => {
              const next = event.target.value
              void setSettings({ sex: next === 'female' || next === 'male' ? next : null })
            }}
            className={field}
          >
            <option value="">Not said</option>
            <option value="female">Female</option>
            <option value="male">Male</option>
          </select>
        </label>
        {/*
          Kept as the field is left rather than on a button of its own: everything else in
          settings is kept as it is changed, and a height that needed a second press was a
          height quietly lost by anyone who closed the panel after typing one.
        */}
        <div className="px-3 py-2">
          <HeightInput
            heightM={settings.heightM}
            units={distanceUnits}
            onHeightM={(heightM) => {
              void setSettings({ heightM })
            }}
          />
        </div>
        <label className="flex items-center justify-between rounded-xl px-3 py-2.5">
          Activity level
          <select
            aria-label="Activity level"
            value={settings.activityLevel}
            onChange={(event) => {
              void setSettings({ activityLevel: event.target.value as ActivityLevel })
            }}
            className={field}
          >
            {ACTIVITY_LEVELS.map((level) => (
              <option key={level} value={level}>
                {ACTIVITY_LABELS[level]}
              </option>
            ))}
          </select>
        </label>
      </fieldset>
      <p className="px-3 pt-2 pb-3 text-xs text-ink-muted">
        What the expenditure is estimated from until there are logged days to correct it with.
      </p>

      <nav className="pb-1">
        <Link to="/diary/foods" search={withoutDialog} className="menu-row">
          <span className="min-w-0 flex-1">Own foods, recipes and meals</span>
          <ChevronRightIcon aria-hidden className="size-6 shrink-0 text-ink-muted" />
        </Link>
        <Link to="/you/goal" search={withoutDialog} className="menu-row">
          {/*
            Whether there is a goal decides both the words here and whether the check-in row
            stands at all, so neither is said until the diary has arrived: a panel that opens
            saying "Set a goal" to somebody who has one has told them something untrue.
          */}
          <span className="min-w-0 flex-1">
            {data === null ? (
              <SkeletonBlock label="Loading your goal">
                <Skeleton className="h-4 w-16 rounded-md" />
              </SkeletonBlock>
            ) : goal === null ? (
              'Set a goal'
            ) : (
              'Goal'
            )}
          </span>
          <ChevronRightIcon aria-hidden className="size-6 shrink-0 text-ink-muted" />
        </Link>
      </nav>

      <fieldset>
        <legend className="px-3 pt-1 pb-1 text-xs text-ink-muted">Food log</legend>
        <label className="flex cursor-pointer items-center justify-between rounded-xl px-3 py-2.5 transition hover:bg-hover">
          Show workouts in the food log
          <input
            type="checkbox"
            aria-label="Show workouts in the food log"
            checked={settings.workoutsInDiary}
            onChange={(event) => {
              void setSettings({ workoutsInDiary: event.target.checked })
            }}
            className="h-5 w-5 accent-accent"
          />
        </label>
      </fieldset>
      <p className="px-3 pt-2 pb-3 text-xs text-ink-muted">
        A day&rsquo;s workouts stand on its food log at the time each began.
      </p>

      {/*
        Only a coached goal is checked in on — see the nutrition spec, The weekly check-in —
        so a goal set by hand is offered no day for one.
      */}
      {data !== null && goal !== null && goal.mode === 'guided' && (
        <label className="flex items-center justify-between rounded-xl px-3 py-2.5">
          Weekly check-in
          <span className="relative grid">
            <select
              aria-label="Review weekday"
              aria-busy={savingWeekday || undefined}
              value={goal.weeklyCheckinWeekday}
              onChange={(event) => {
                if (!savingWeekday) {
                  void changeWeekday(Number(event.target.value))
                }
              }}
              // Its words give way to the spinner; the field itself stays, in reach of a reader.
              className={`${field} ${weekdayShown ? 'text-transparent' : ''}`}
            >
              {weekOrder().map((day) => (
                <option key={day.weekday} value={day.weekday}>
                  {day.name}
                </option>
              ))}
            </select>
            {weekdayShown && (
              <span className="pointer-events-none absolute inset-0 grid place-items-center">
                <Spinner />
              </span>
            )}
          </span>
        </label>
      )}
    </>
  )
}

/*
 * Two questions, not one. Plenty of people lift in kilograms and run in miles — Britain does
 * it as a matter of course — so answering one leaves the other exactly as it was.
 */
function UnitsSection() {
  const { units, setUnits, distanceUnits, setDistanceUnits } = useAuth()

  return (
    <>
      <fieldset>
        <legend className="px-3 pt-1 pb-1 text-xs text-ink-muted">Weight</legend>
        <UnitRow
          group="units"
          label="Kilograms"
          hint="kg"
          checked={units === 'kg'}
          onSelect={() => {
            void setUnits('kg')
          }}
        />
        <UnitRow
          group="units"
          label="Pounds"
          hint="lb"
          checked={units === 'lb'}
          onSelect={() => {
            void setUnits('lb')
          }}
        />
      </fieldset>
      <p className="px-3 pt-1 pb-3 text-xs text-ink-muted">
        Applied wherever weight is shown or entered.
      </p>

      <fieldset>
        <legend className="px-3 pt-1 pb-1 text-xs text-ink-muted">Distance</legend>
        <UnitRow
          group="distance-units"
          label="Kilometres"
          hint="km"
          checked={distanceUnits === 'km'}
          onSelect={() => {
            void setDistanceUnits('km')
          }}
        />
        <UnitRow
          group="distance-units"
          label="Miles"
          hint="mi"
          checked={distanceUnits === 'mi'}
          onSelect={() => {
            void setDistanceUnits('mi')
          }}
        />
      </fieldset>
      <p className="px-3 pt-1 pb-2 text-xs text-ink-muted">
        Applied wherever distance is shown or entered, pace included.
      </p>
    </>
  )
}

/**
 * Faces in videos that leave the app — `/specs/features/faces.spec.md`. Both belong to the
 * account, so a choice made here is the one the phone follows too.
 */
function VideosSection() {
  const { settings, setSettings } = useAuth()

  return (
    <>
      <fieldset>
        <legend className="px-3 pt-1 pb-1 text-xs text-ink-muted">Faces in shared videos</legend>
        {FACES_DEFAULTS.map((each) => (
          <UnitRow
            key={each}
            group="share-faces"
            label={START_LABELS[each]}
            hint=""
            checked={settings.shareFaces === each}
            onSelect={() => {
              void setSettings({ shareFaces: each })
            }}
          />
        ))}
      </fieldset>
      <p className="px-3 pt-1 pb-3 text-xs text-ink-muted">
        Whose faces a video you download starts with blurred. Nothing you watch here is blurred.
      </p>

      <fieldset>
        <label className="flex cursor-pointer items-center justify-between rounded-xl px-3 py-2.5 transition hover:bg-hover">
          Ask before sharing
          <input
            type="checkbox"
            aria-label="Ask before sharing"
            checked={settings.shareFacesAsk}
            onChange={(event) => {
              void setSettings({ shareFacesAsk: event.target.checked })
            }}
            className="h-5 w-5 accent-accent"
          />
        </label>
      </fieldset>
      <p className="px-3 pt-1 pb-2 text-xs text-ink-muted">
        Show a video’s faces before it is downloaded, to choose whose are blurred.
      </p>
    </>
  )
}

function UnitRow({
  group,
  label,
  hint,
  checked,
  onSelect,
}: {
  /** Which question this row answers, so the two sets of radios are two choices. */
  group: string
  label: string
  hint: string
  checked: boolean
  onSelect: () => void
}) {
  return (
    <label className="flex cursor-pointer items-center justify-between rounded-xl px-3 py-2.5 transition hover:bg-hover">
      <span className="flex items-baseline gap-2">
        {label}
        <span className="text-xs text-ink-muted">{hint}</span>
      </span>
      <input
        type="radio"
        name={group}
        aria-label={label}
        checked={checked}
        onChange={onSelect}
        className="h-5 w-5 accent-accent"
      />
    </label>
  )
}

/**
 * How much room the videos take up, and how much the plan allows. Both come with the account
 * rather than from a read of their own, so the only answer that is not a size is the one for
 * a device that has not read the account back yet.
 */
function StorageSection() {
  const { plan } = useAuth()

  return (
    <div className="flex items-center justify-between gap-4 px-3 py-3">
      <p className="text-sm text-ink-muted">Storage used by your videos</p>
      {plan === null ? (
        <SkeletonBlock label="Working out how much room your videos take up">
          <Skeleton className="h-4 w-20 rounded-md" />
        </SkeletonBlock>
      ) : (
        <p className="text-sm font-medium">
          {formatBytes(plan.storageUsedBytes)} of {formatBytes(plan.storageBytes)}
        </p>
      )}
    </div>
  )
}

function DeleteAccountSection() {
  const { deleteAccount } = useAuth()
  const [confirming, setConfirming] = useState(false)

  return (
    <div className="space-y-3 px-3 py-3">
      <p className="text-sm text-ink-muted">
        Deleting the account removes it and everything in it. There is no undo.
      </p>
      <button
        type="button"
        className="rounded-full border border-danger/40 px-4 py-2 text-sm font-medium text-danger transition hover:bg-danger/10"
        onClick={() => {
          setConfirming(true)
        }}
      >
        Delete account
      </button>

      {confirming ? (
        <ConfirmDialog
          title="Delete your account?"
          body="Every set, workout, video and template goes with it. There is no undo."
          confirm="Delete everything"
          onConfirm={async () => {
            await reached(() => deleteAccount())
          }}
          onDismiss={() => {
            setConfirming(false)
          }}
        />
      ) : null}
    </div>
  )
}
